import { useState } from 'react'
import { MakerCheckerBar } from './MakerCheckerBar'
import { RuleSetPicker } from './RuleSetPicker'
import { ParameterEditor } from './ParameterEditor'
import { approveRuleSet, publishRuleSet, submitRuleSetForReview } from '../../../api/cartridgeStudio'
import { ApiError } from '../../../api/problem'
import type { RuleSetView } from '../../../types/cartridgeStudio'
import './ParametersTab.css'

export interface ParametersTabProps {
  programId: string
}

/** Design doc §4.1 — `RuleSetPicker` + `MakerCheckerBar` (bound to the selected RuleSet) + `ParameterEditor`. */
export function ParametersTab({ programId }: ParametersTabProps) {
  const [selected, setSelected] = useState<RuleSetView | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState(0)

  function violationsText(err: unknown, fallback: string): string {
    if (err instanceof ApiError) {
      const violations = err.problem.violations
      if (violations && violations.length > 0) return violations.map((v) => v.message ?? v.code).join('; ')
      return err.problem.detail ?? err.message
    }
    return fallback
  }

  async function runTransition(fn: (id: string) => Promise<RuleSetView>) {
    if (!selected) return
    setBusy(true)
    setError(null)
    try {
      const updated = await fn(selected.id)
      setSelected(updated)
      setRefreshToken((t) => t + 1)
    } catch (err) {
      setError(violationsText(err, 'Could not update this RuleSet.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="iw-parameters-tab">
      <RuleSetPicker programId={programId} selectedId={selected?.id ?? null} onSelect={setSelected} refreshToken={refreshToken} />
      <div className="iw-parameters-tab__main">
        {selected ? (
          <>
            <MakerCheckerBar
              artifactLabel={`RuleSet v${selected.structureVersion}`}
              variant="lifecycle"
              status={selected.status}
              busy={busy}
              errorMessage={error}
              onSubmitForReview={() => void runTransition((id) => submitRuleSetForReview(id, { comment: null }))}
              onApprove={() => void runTransition((id) => approveRuleSet(id, { comment: null }))}
              onPublish={() => void runTransition((id) => publishRuleSet(id, { comment: null }))}
              onNewVersion={() => setSelected(null)}
            />
            <ParameterEditor ruleSetId={selected.id} />
          </>
        ) : (
          <div className="iw-parameters-tab__empty">Select or create a RuleSet to author its parameters.</div>
        )}
      </div>
    </div>
  )
}
